<template>
  <div class="main-content">
    <!-- 第一级：标签栏（阶段/范围） -->
    <div class="stage-tabs">
      <div
        v-for="stageKey in stageKeys"
        :key="stageKey"
        class="stage-tab"
        :class="{ 'stage-tab-active': currentStageKey === stageKey }"
        @click="switchStage(stageKey)"
      >
        {{ dataCalculateConfig[stageKey]?.label }}
      </div>
    </div>

    <!-- 数据计算内容区域 -->
    <div class="calculate-content">
      <!-- 第二级：分类列表 -->
      <div
        v-for="(category, categoryKey) in currentStageCategoriesWithItems"
        :key="categoryKey"
        class="calculate-category"
      >
        <!-- 分类标题（可折叠） -->
        <div
          class="category-header"
          :class="{
            'category-expanded': isCategoryExpanded(
              currentStageKey,
              categoryKey
            ),
          }"
          @click="toggleCategory(currentStageKey, categoryKey)"
        >
          <div class="category-arrow">
            <img
              :src="
                isCategoryExpanded(currentStageKey, categoryKey)
                  ? require('@/assets/images/chatpreview/arrow-down.svg')
                  : require('@/assets/images/chatpreview/arrow-right.svg')
              "
              alt="arrow"
            />
          </div>
          <div class="category-label">{{ category.label }}</div>
          <!-- 分类汇总数据 -->
          <div class="category-total" v-if="category.total_value">
            <!-- <img
              :src="
                isCategoryExpanded(currentStageKey, categoryKey)
                  ? require('@/assets/images/chatpreview/data_calculate_ye_e.svg')
                  : require('@/assets/images/chatpreview/data_calculate_ye.svg')
              "
              alt="leaf"
              class="total-icon"
            /> -->
            <span class="total-value">{{ category.total_value }}</span>
          </div>
        </div>

        <!-- 第三级：分类下的计算项列表 -->
        <div
          class="category-items"
          v-show="isCategoryExpanded(currentStageKey, categoryKey)"
        >
          <div
            v-for="(calculateItem, calculateItemIndex) in getCalculateItems(
              category
            )"
            :key="calculateItemIndex"
            class="calculate-item"
            :class="{
              'calculate-item-missing': calculateItem.status === 'error',
            }"
          >
            <!-- 第一列：名称 -->
            <div class="item-name-section">
              <el-tooltip
                :content="calculateItem.name"
                placement="bottom"
                effect="light"
                :open-delay="300"
                popper-class="chat-preview-tooltip"
              >
                <div class="item-name">{{ calculateItem.name }}</div>
              </el-tooltip>
              <div
                v-if="calculateItem.status === 'error'"
                class="status-icon-wrapper"
                @mouseleave="hideHelp(currentStageKey, categoryKey, calculateItemIndex)"
              >
                <img
                  src="@/assets/images/chatpreview/data_calculate_doubt.svg"
                  alt="doubt"
                  class="status-icon"
                  @click.stop="showHelp(currentStageKey, categoryKey, calculateItemIndex)"
                />

                <!-- 帮助提示框 -->
                <div
                  v-show="showHelpItemKey === `${currentStageKey}-${categoryKey}-${calculateItemIndex}`"
                  class="help-tooltip"
                  :class="getTooltipPositionClass(currentStageKey, categoryKey, calculateItemIndex)"
                  ref="tooltip"
                >
                  {{ calculateItem.message }}
                </div>
              </div>
            </div>
            <!-- 第二列：位置 -->
            <el-tooltip
              :content="calculateItem.location"
              placement="bottom"
              effect="light"
              :open-delay="300"
              popper-class="chat-preview-tooltip"
            >
              <div class="item-location">{{ calculateItem.location }}</div>
            </el-tooltip>
            <!-- 第三列：结果 -->
            <el-tooltip
              :content="calculateItem.result ? calculateItem.result + calculateItem.unit : '--' + calculateItem.unit"
              placement="bottom"
              effect="light"
              :open-delay="300"
              popper-class="chat-preview-tooltip"
            >
                <div class="item-result">
                  {{ calculateItem.result || '--' }}{{ calculateItem.unit }}
                </div>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "DataCalculate",
  props: {
    previewData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      // 第一级：当前选中的阶段/范围标签页
      currentStageKey: null,
      // 第二级：已展开的分类（按阶段分组）
      expandedCategories: {},
      // 第三级：已展开的计算项（按分类分组）
      expandedCalculateItems: {},
      showHelpItemKey: null, // 当前显示的帮助项 key
      tooltipPosition: { vertical: 'bottom' }, // 提示框位置
    };
  },
  computed: {
    ...mapState(["accessType"]),

    dataCalculateConfig() {
      // 如果 previewData 存在，优先使用预览数据，否则使用配置数据
      if (this.previewData) {
        return this.previewData;
      }
      return {};
    },
    // 第一级：获取所有阶段/范围标签页的键（按顺序），只返回有数据的阶段
    stageKeys() {
      const config = this.dataCalculateConfig;
      if (!config) return [];

      let keys = [];
      if (this.accessType === "tpc") {
        keys = ["scope_1", "scope_2", "scope_3"];
      } else if (this.accessType === "tzj") {
        keys = [
          "raw_material_acquisition_stage",
          "raw_material_transportation_stage",
          "production_manufacturing_stage",
          "transportation_and_storage_stage",
        ];
      }
      
      // 过滤：只返回有实际数据的阶段（有分类且分类下有计算项）
      return keys.filter((key) => {
        const stage = config[key];
        if (!stage) return false;
        
        // 检查该阶段是否有分类和计算项数据
        const categories = this.getCategoriesForStage(stage);
        for (const categoryKey in categories) {
          const category = categories[categoryKey];
          const calculateItems = this.getCalculateItems(category);
          if (calculateItems && calculateItems.length > 0) {
            return true; // 找到有数据的分类，该阶段应该显示
          }
        }
        return false; // 没有找到有数据的分类，该阶段不显示
      });
    },
    // 第二级：获取当前阶段下有计算项的分类列表（过滤掉空分类）
    currentStageCategoriesWithItems() {
      const categories = this.getCurrentStageCategories();
      const filteredCategories = {};
      for (const categoryKey in categories) {
        const category = categories[categoryKey];
        const calculateItems = this.getCalculateItems(category);
        if (calculateItems && calculateItems.length > 0) {
          filteredCategories[categoryKey] = category;
        }
      }
      return filteredCategories;
    },
  },
  watch: {
    // 监听配置变化，自动初始化默认选中和展开状态
    dataCalculateConfig: {
      immediate: true,
      deep: true,
      handler(newConfig) {
        if (!newConfig || Object.keys(newConfig).length === 0) {
          return;
        }
        this.initializeDefaultState();
      },
    },
  },
  mounted() {
    // 组件挂载时，如果配置已存在，初始化默认状态
    if (
      this.dataCalculateConfig &&
      Object.keys(this.dataCalculateConfig).length > 0
    ) {
      this.initializeDefaultState();
    }
  },
  methods: {
    // 初始化默认状态（默认选中第一个阶段标签页）
    initializeDefaultState() {
      const config = this.dataCalculateConfig;
      if (!config || Object.keys(config).length === 0) {
        return;
      }

      // 第一级：获取第一个阶段标签页的键
      const firstStageKey = this.stageKeys[0];
      if (!firstStageKey) {
        return;
      }

      // 设置默认选中的阶段标签页
      if (this.currentStageKey !== firstStageKey) {
        this.currentStageKey = firstStageKey;
      }

      // 初始化所有阶段的展开状态
      this.stageKeys.forEach((stageKey) => {
        // 第二级：初始化分类展开状态
        if (!this.expandedCategories[stageKey]) {
          this.$set(this.expandedCategories, stageKey, []);
        }
      });
    },
    // 第二级：获取阶段下的分类列表（适配TPC和TZJ两种结构）
    getCategoriesForStage(stage) {
      if (!stage) return {};
      const categories = {};
      for (const key in stage) {
        // 排除 label 键
        if (key === "label") {
          continue;
        }

        const item = stage[key];
        // 只处理对象类型
        if (typeof item !== "object" || item === null) {
          continue;
        }

        // TZJ: 排除 value 是字符串的情况（如 raw_material_acquisition_stage.value = ""）
        // TPC: value 是数组，不会被排除
        if ("value" in item && typeof item.value === "string") {
          continue;
        }

        categories[key] = item;
      }
      return categories;
    },
    // 第一级：切换阶段/范围标签页
    switchStage(stageKey) {
      this.currentStageKey = stageKey;
      // 如果当前阶段没有展开的分类，初始化展开状态
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
    },
    // 第二级：获取当前阶段下的分类列表
    getCurrentStageCategories() {
      if (!this.currentStageKey) return {};
      const stage = this.dataCalculateConfig[this.currentStageKey];
      if (!stage) return {};
      return this.getCategoriesForStage(stage);
    },
    // 第三级：获取分类下的计算项列表
    getCalculateItems(category) {
      if (!category || !Array.isArray(category.value)) {
        return [];
      }
      return category.value;
    },
    // 第二级：判断分类是否展开
    isCategoryExpanded(stageKey, categoryKey) {
      return (
        this.expandedCategories[stageKey] &&
        this.expandedCategories[stageKey].includes(categoryKey)
      );
    },
    // 第二级：切换分类展开/收起
    toggleCategory(stageKey, categoryKey) {
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
      const categories = this.expandedCategories[stageKey];
      const index = categories.indexOf(categoryKey);
      if (index > -1) {
        categories.splice(index, 1);
      } else {
        categories.push(categoryKey);
      }
    },
    // 显示帮助信息
    showHelp(stageKey, categoryKey, itemIndex) {
      // 生成唯一标识符：stageKey-categoryKey-itemIndex
      const uniqueKey = `${stageKey}-${categoryKey}-${itemIndex}`;
      // 如果点击的是当前显示的项，则隐藏；否则显示
      if (this.showHelpItemKey === uniqueKey) {
        this.showHelpItemKey = null;
        // 重置位置状态
        this.tooltipPosition = { vertical: 'bottom' };
      } else {
        // 先重置位置状态为默认值
        this.tooltipPosition = { vertical: 'bottom' };
        // 显示新的提示框
        this.showHelpItemKey = uniqueKey;
        // 延迟计算位置，确保DOM已更新
        this.$nextTick(() => {
          this.calculateTooltipPosition(stageKey, categoryKey, itemIndex);
        });
      }
    },
    // 隐藏帮助信息
    hideHelp(stageKey, categoryKey, itemIndex) {
      // 生成唯一标识符：stageKey-categoryKey-itemIndex
      const uniqueKey = `${stageKey}-${categoryKey}-${itemIndex}`;
      // 只有当鼠标离开的是当前显示的项时，才隐藏
      if (this.showHelpItemKey === uniqueKey) {
        this.showHelpItemKey = null;
      }
    },
    // 计算提示框位置
    calculateTooltipPosition(stageKey, categoryKey, itemIndex) {
      const uniqueKey = `${stageKey}-${categoryKey}-${itemIndex}`;
      if (this.showHelpItemKey !== uniqueKey) return;
      
      // 再次检查是否还需要显示这个提示框
      if (this.showHelpItemKey !== uniqueKey) return;
      
      // 获取当前显示的提示框元素
      const tooltipElements = this.$refs.tooltip;
      if (!tooltipElements) return;
      
      // 如果是数组，找到对应的元素；如果是单个元素，直接使用
      let tooltipElement = tooltipElements;
      if (Array.isArray(tooltipElements)) {
        // 通过DOM查找当前显示的提示框
        tooltipElement = tooltipElements.find(el => el && el.offsetParent !== null);
      }
      
      if (!tooltipElement) return;
      
      // 获取提示框的边界信息
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();
      
      // 检查垂直下方向是否超出边界
      const isOverflowBottom = tooltipRect.bottom > containerRect.bottom;
      
      // 根据边界情况设置位置
      let vertical = 'bottom';
      if (isOverflowBottom) {
        vertical = 'top';
      }
      
      this.tooltipPosition = { vertical };
    },
    // 获取帮助提示框位置类
    getTooltipPositionClass(stageKey, categoryKey, itemIndex) {
      const uniqueKey = `${stageKey}-${categoryKey}-${itemIndex}`;
      if (this.showHelpItemKey === uniqueKey) {
        return `help-tooltip tooltip-${this.tooltipPosition.vertical}`;
      }
      return '';
    },
  },
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
  min-height: 269px;
  max-height: 473px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
}

// 第一级：阶段/范围标签页样式
.stage-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
}

.stage-tab {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(5, 32, 49, 1);
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
}

.stage-tab-active {
  background-color: rgba(219, 232, 245, 1);
}

// 数据计算内容区域
.calculate-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    display: none;
  }
}

.calculate-category {
  width: 100%;
}

// 第二级：分类标题样式
.category-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;
  padding-right: 10px;

  &:hover {
    background: rgba(244, 248, 252, 1);
  }
}

.category-header.category-expanded {
  background: rgba(219, 232, 245, 1);
  border-radius: 8px;
}

.category-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.category-label {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  .category-header:not(.category-expanded) & {
    color: rgba(170, 179, 187, 1);
  }
}

// 分类汇总数据样式
.category-total {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
}

.total-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.total-value {
  font-size: 14px;
  font-weight: 700;
  color: rgba(170, 179, 187, 1);

  .category-header.category-expanded & {
    font-weight: 600;
    color: rgba(5, 32, 49, 1);
  }
}

// 第三级：分类下的计算项列表
.category-items {
  width: 100%;
  padding-left: 24px;
  padding-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.calculate-item {
  display: flex;
  gap: 10px;
  flex-direction: row;
  align-items: center;
  // justify-content: space-between;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(5, 32, 49, 1);
  border-radius: 4px;
}

.calculate-item-missing {
  color: rgba(248, 177, 177, 1);
  background-color: rgba(248, 177, 177, 0.2);
}

.status-icon-wrapper {
  // flex: 0.2;
  position: relative;
  width: 16px;
  height: 16px;
}

.status-icon {
  opacity: 0;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  object-fit: contain;
}

// hover 列表项时显示图标
.calculate-item-missing:hover .status-icon {
  opacity: 1;
}

// hover 图标时显示深色版本
.status-icon-wrapper .status-icon:hover {
  opacity: 1;
  cursor: pointer;
  // 显示深红色
  filter: brightness(0) saturate(100%) invert(34%) sepia(88%) saturate(1627%) hue-rotate(357deg) brightness(98%) contrast(98%);
}

.item-name-section {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;

  .item-name {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.item-location {
  flex: 1.5;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-result {
  flex: 1;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-tooltip {
  min-width:80px;
  max-width: 380px;
  position: absolute;
  font-size: 10px;
  color: rgba(5, 32, 49, 1);
  z-index: 1000;
  text-align: justify;
  line-height: 1.5;
  padding: 8px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgb(0, 0, 0);
  border-radius: 6px;
  background: rgba(255, 255, 255, 1);
  word-wrap: break-word;
  white-space: normal;
  transition: all 0.2s ease;
}

/* 垂直位置 */
.help-tooltip.tooltip-bottom {
  top: 100%;
}

.help-tooltip.tooltip-top {
  bottom: 100%;
}
</style>
